import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ScheduleRecommendationService } from '@/lib/services/schedule-recommendation-service';
import { logger } from '@/lib/services/logger-service';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';

// Define the sleep schedule data types
export type ScheduleStatus = 'on-time' | 'late' | 'short' | 'missed' | 'off-schedule' | 'not-logged';

export interface ScheduleItemMetadata {
  matchedLogId?: string;
  closestOutsideThreshold?: {
    id: string;
    diffMinutes: number;
  };
  hasAnyLogsForDay?: boolean;
}

export interface ScheduleItem {
  id: string;
  type: 'wake' | 'nap' | 'bedtime';
  label: string;
  scheduledStartTime: string;
  scheduledEndTime?: string;
  actualStartTime?: string;
  actualEndTime?: string;
  status: ScheduleStatus;
  notes?: string;
  duration?: string;
  targetDuration?: string;
  meta?: ScheduleItemMetadata;
}

export interface SleepDuration {
  hours: number;
  minutes: number;
}

export interface SleepMetricsItem {
  actual: SleepDuration;
  target: {
    minHours: number;
    maxHours: number;
  };
}

export interface SleepMetrics {
  daySlept: SleepMetricsItem;
  nightSlept: SleepMetricsItem;
  totalSlept: SleepMetricsItem;
}

interface ChildInfo {
  name: string;
  dateOfBirth: string;
}

interface SleepScheduleContextType {
  scheduleItems: ScheduleItem[];
  setScheduleItems: React.Dispatch<React.SetStateAction<ScheduleItem[]>>;
  totalNapTime: { actual: number; scheduled: number };
  wakeWindowsOnTime: { completed: number; total: number };
  bedtimeConsistency: { onTime: number; total: number };
  sleepMetrics: SleepMetrics;
  childInfo: ChildInfo | null;
  caregiverId: string | null;
  loadScheduleForDate: (date: Date, childId?: string) => Promise<void>;
  generateAIRecommendation: (childId: string, date?: Date) => Promise<void>;
  createScheduleItem: (item: Partial<ScheduleItem>) => Promise<ScheduleItem>;
  updateSchedule: (items: ScheduleItem[], childId?: string, caregiverId?: string, repeatDays?: number) => Promise<void>;
  resetToRecommendations: (childId: string, date?: Date) => Promise<void>;
  isSpecialist: boolean;
  isLoadingRecommendation: boolean;
  hasCustomSchedule: boolean;
}

const defaultContext: SleepScheduleContextType = {
  scheduleItems: [],
  setScheduleItems: () => {},
  totalNapTime: { actual: 0, scheduled: 0 },
  wakeWindowsOnTime: { completed: 0, total: 0 },
  bedtimeConsistency: { onTime: 0, total: 0 },
  sleepMetrics: {
    daySlept: {
      actual: { hours: 0, minutes: 0 },
      target: { minHours: 0, maxHours: 0 }
    },
    nightSlept: {
      actual: { hours: 11, minutes: 30 },
      target: { minHours: 11, maxHours: 12 }
    },
    totalSlept: {
      actual: { hours: 11, minutes: 30 },
      target: { minHours: 11, maxHours: 14 }
    }
  },
  childInfo: null,
  caregiverId: null,
  loadScheduleForDate: async () => {},
  generateAIRecommendation: async () => {},
  createScheduleItem: async () => ({ id: '', type: 'wake', label: '', scheduledStartTime: '', status: 'on-time' }),
  updateSchedule: async () => {},
  resetToRecommendations: async () => {},
  isSpecialist: false,
  isLoadingRecommendation: false,
  hasCustomSchedule: false,
};

const SleepScheduleContext = createContext<SleepScheduleContextType>(defaultContext);

const LOG_PREFIX = '[SleepScheduleProvider]';

// No mock data - all schedules come from database or AI recommendations

// Calculate various metrics from schedule items
const calculateMetrics = (items: ScheduleItem[]) => {
  // Calculate total nap time (actual vs scheduled)
  const napItems = items.filter(item => item.type === 'nap');
  
  let actualNapMinutes = 0;
  let scheduledNapMinutes = 0;
  
  napItems.forEach(nap => {
    // Calculate scheduled nap time
    if (nap.scheduledStartTime && nap.scheduledEndTime) {
      const scheduledStart = new Date(nap.scheduledStartTime);
      const scheduledEnd = new Date(nap.scheduledEndTime);
      scheduledNapMinutes += (scheduledEnd.getTime() - scheduledStart.getTime()) / (1000 * 60);
    }
    
    // Calculate actual nap time
    if (nap.actualStartTime && nap.actualEndTime) {
      const actualStart = new Date(nap.actualStartTime);
      const actualEnd = new Date(nap.actualEndTime);
      actualNapMinutes += (actualEnd.getTime() - actualStart.getTime()) / (1000 * 60);
    }
  });
  
  // Calculate wake window adherence
  const wakeItems = items.filter(item => item.type === 'wake' && item.id !== '1'); // Exclude rise time
  const wakeWindowsOnTime = wakeItems.filter(item => item.status === 'on-time').length;
  
  // Calculate bedtime consistency
  const bedtimeItems = items.filter(item => item.type === 'bedtime');
  const bedtimeOnTime = bedtimeItems.filter(item => item.status === 'on-time').length;
  
  return {
    totalNapTime: {
      actual: actualNapMinutes,
      scheduled: scheduledNapMinutes
    },
    wakeWindowsOnTime: {
      completed: wakeWindowsOnTime,
      total: wakeItems.length
    },
    bedtimeConsistency: {
      onTime: bedtimeOnTime,
      total: bedtimeItems.length
    }
  };
};

// Calculate actual sleep metrics from sessions
const calculateSleepMetrics = (
  sleepSessions: any[], 
  napSessions: any[],
  childAgeMonths?: number
): SleepMetrics => {
  // Calculate total nap duration (day sleep)
  const totalNapMinutes = napSessions.reduce((sum, nap) => {
    return sum + (nap.total_duration || 0);
  }, 0);

  // Calculate night sleep duration
  const totalNightMinutes = sleepSessions.reduce((sum, sleep) => {
    return sum + (sleep.total_duration || 0);
  }, 0);

  // Total sleep is day + night
  const totalMinutes = totalNapMinutes + totalNightMinutes;

  // Age-appropriate targets (simplified - in real app, use wake_window_guidelines)
  const getTargets = (ageMonths: number = 6) => {
    if (ageMonths < 4) {
      return { dayMin: 4, dayMax: 6, nightMin: 8, nightMax: 10, totalMin: 14, totalMax: 17 };
    } else if (ageMonths < 12) {
      return { dayMin: 2, dayMax: 4, nightMin: 10, nightMax: 12, totalMin: 12, totalMax: 15 };
    } else if (ageMonths < 24) {
      return { dayMin: 1, dayMax: 3, nightMin: 11, nightMax: 12, totalMin: 12, totalMax: 14 };
    } else {
      return { dayMin: 1, dayMax: 2, nightMin: 10, nightMax: 12, totalMin: 11, totalMax: 14 };
    }
  };

  const targets = getTargets(childAgeMonths);

  return {
    daySlept: {
      actual: { 
        hours: Math.floor(totalNapMinutes / 60), 
        minutes: totalNapMinutes % 60 
      },
      target: { minHours: targets.dayMin, maxHours: targets.dayMax }
    },
    nightSlept: {
      actual: { 
        hours: Math.floor(totalNightMinutes / 60), 
        minutes: totalNightMinutes % 60 
      },
      target: { minHours: targets.nightMin, maxHours: targets.nightMax }
    },
    totalSlept: {
      actual: { 
        hours: Math.floor(totalMinutes / 60), 
        minutes: totalMinutes % 60 
      },
      target: { minHours: targets.totalMin, maxHours: targets.totalMax }
    }
  };
};

interface SleepScheduleProviderProps {
  children: ReactNode;
  isSpecialist?: boolean;
  caregiverId?: string;
}

export function SleepScheduleProvider({ children, isSpecialist = false, caregiverId: caregiverIdProp }: SleepScheduleProviderProps) {
  const { caregiverId: authCaregiverId } = useAuth();
  const caregiverId = caregiverIdProp || authCaregiverId;
  
  React.useEffect(() => {
    logger.info(`${LOG_PREFIX} Initialized`, {
      caregiverIdProp,
      authCaregiverId,
      effectiveCaregiverId: caregiverId,
      isSpecialist,
      hasPropCaregiver: !!caregiverIdProp,
      hasAuthCaregiver: !!authCaregiverId
    });
  }, []);
  
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [customSchedules, setCustomSchedules] = useState<Map<string, ScheduleItem[]>>(new Map());
  const [isLoadingRecommendation, setIsLoadingRecommendation] = useState(false);
  const [hasCustomSchedule, setHasCustomSchedule] = useState(false);
  const [childInfo, setChildInfo] = useState<ChildInfo | null>(null);
  const [caregiverIdState, setCaregiverIdState] = useState<string | null>(caregiverId || null);
  const [metrics, setMetrics] = useState({
    totalNapTime: { actual: 0, scheduled: 0 },
    wakeWindowsOnTime: { completed: 0, total: 0 },
    bedtimeConsistency: { onTime: 0, total: 0 }
  });
  
  const [sleepMetrics, setSleepMetrics] = useState<SleepMetrics>({
    daySlept: { actual: { hours: 0, minutes: 0 }, target: { minHours: 2, maxHours: 4 } },
    nightSlept: { actual: { hours: 0, minutes: 0 }, target: { minHours: 10, maxHours: 12 } },
    totalSlept: { actual: { hours: 0, minutes: 0 }, target: { minHours: 12, maxHours: 15 } }
  });
  
  const loadScheduleForDate = async (date: Date, childId?: string) => {
    const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    
    logger.info(`${LOG_PREFIX} loadScheduleForDate called`, {
      dateKey,
      childId,
      hasChildId: !!childId,
      caregiverIdState,
      effectiveCaregiverId: caregiverId
    });
    
    try {
      let scheduleToUse: ScheduleItem[] = [];
      
      // Load child information if childId is provided
      if (childId) {
        logger.debug(`${LOG_PREFIX} Loading child information`, { childId });
        
        const { data: childData, error: childError } = await supabase
          .from('children')
          .select('name, date_of_birth, caregiver_id')
          .eq('id', childId)
          .single();
        
        if (childError) {
          logger.error(`${LOG_PREFIX} Error loading child data`, { error: childError, childId });
        }
        
        if (!childError && childData) {
          logger.info(`${LOG_PREFIX} Child data loaded`, {
            childId,
            childName: childData.name,
            caregiverId: childData.caregiver_id,
            dateOfBirth: childData.date_of_birth
          });
          
          setChildInfo({
            name: childData.name,
            dateOfBirth: childData.date_of_birth
          });
          if (childData.caregiver_id) {
            logger.debug(`${LOG_PREFIX} Setting caregiver from child`, { caregiverId: childData.caregiver_id });
            setCaregiverIdState(childData.caregiver_id);
          }
        }
      } else {
        logger.debug(`${LOG_PREFIX} No childId, clearing child info`);
        setChildInfo(null);
        setCaregiverIdState(caregiverId || null);
      }
      
      // Try to load custom schedule from database if childId is provided
      if (childId) {
        logger.debug(`${LOG_PREFIX} Querying custom_sleep_schedules`, { childId, dateKey });
        
        const { data: customSchedule, error } = await supabase
          .from('custom_sleep_schedules' as any)
          .select('*')
          .eq('child_id', childId)
          .eq('schedule_date', dateKey)
          .maybeSingle();

        if (error) {
          logger.error(`${LOG_PREFIX} Error loading custom schedule`, { error, childId, dateKey });
        } else if (customSchedule) {
          scheduleToUse = (customSchedule as any).schedule_items as ScheduleItem[];
          logger.info(`${LOG_PREFIX} Custom schedule loaded`, {
            childId,
            dateKey,
            itemCount: scheduleToUse.length,
            scheduleId: (customSchedule as any).id
          });
          setHasCustomSchedule(true);
          
          // Cache in local state
          setCustomSchedules(prev => {
            const newMap = new Map(prev);
            newMap.set(dateKey, scheduleToUse);
            return newMap;
          });
        } else if (customSchedules.has(dateKey)) {
          // Check cached custom schedule
          scheduleToUse = customSchedules.get(dateKey)!;
          logger.debug(`${LOG_PREFIX} Using cached schedule`, { dateKey, itemCount: scheduleToUse.length });
          setHasCustomSchedule(true);
        } else {
          // No custom schedule - empty schedule
          logger.info(`${LOG_PREFIX} No custom schedule found`, { childId, dateKey });
          scheduleToUse = [];
          setHasCustomSchedule(false);
        }

        // Load actual sleep/nap sessions for comparison
        logger.debug(`${LOG_PREFIX} Loading actual sessions`, { childId, dateKey });
        await loadActualSessions(childId, date, scheduleToUse);
      } else {
        // No child ID - show empty schedule
        if (customSchedules.has(dateKey)) {
          scheduleToUse = customSchedules.get(dateKey)!;
          logger.debug(`${LOG_PREFIX} Using cached schedule (no child)`, { dateKey });
          setHasCustomSchedule(true);
        } else {
          logger.info(`${LOG_PREFIX} No schedule available (no child)`, { dateKey });
          scheduleToUse = [];
          setHasCustomSchedule(false);
        }
        setScheduleItems(scheduleToUse);
      }
      
      const newMetrics = calculateMetrics(scheduleToUse);
      logger.debug(`${LOG_PREFIX} Metrics calculated`, { metrics: newMetrics });
      setMetrics(newMetrics);
      // Sleep metrics will be calculated when actual sessions are loaded
    } catch (error) {
      logger.error(`${LOG_PREFIX} Exception in loadScheduleForDate`, { error, dateKey, childId });
      // Fallback to empty schedule
      setScheduleItems([]);
      setHasCustomSchedule(false);
      setMetrics({
        totalNapTime: { actual: 0, scheduled: 0 },
        wakeWindowsOnTime: { completed: 0, total: 0 },
        bedtimeConsistency: { onTime: 0, total: 0 }
      });
      setSleepMetrics({
        daySlept: { actual: { hours: 0, minutes: 0 }, target: { minHours: 2, maxHours: 4 } },
        nightSlept: { actual: { hours: 0, minutes: 0 }, target: { minHours: 10, maxHours: 12 } },
        totalSlept: { actual: { hours: 0, minutes: 0 }, target: { minHours: 12, maxHours: 15 } }
      });
    }
  };

  const loadActualSessions = async (childId: string, date: Date, plannedSchedule: ScheduleItem[]) => {
    // Create a ±2 day window to capture overnight sleep and handle timezone edge cases
    const queryStart = new Date(date);
    queryStart.setDate(queryStart.getDate() - 2);  // 2 days in the past
    queryStart.setHours(0, 0, 0, 0);
    
    const queryEnd = new Date(date);
    queryEnd.setDate(queryEnd.getDate() + 2);  // 2 days in the future
    queryEnd.setHours(23, 59, 59, 999);

    try {
      // Load child age for metrics
      const { data: childData } = await supabase
        .from('children')
        .select('date_of_birth')
        .eq('id', childId)
        .single();

      let childAgeMonths = 6; // default
      if (childData?.date_of_birth) {
        const birthDate = new Date(childData.date_of_birth);
        const ageMs = date.getTime() - birthDate.getTime();
        childAgeMonths = Math.floor(ageMs / (1000 * 60 * 60 * 24 * 30.44));
      }

      // Load actual sleep sessions (bedtime/overnight sleep)
      const { data: sleepSessions, error: sleepError } = await supabase
      .from('sleep_sessions')
      .select('*')
      .eq('child_id', childId)
      .gte('start_time', queryStart.toISOString())
      .lte('start_time', queryEnd.toISOString())
        .order('start_time', { ascending: true });

      // Load actual nap sessions (daytime sleep)
      const { data: napSessions, error: napError } = await supabase
      .from('nap_sessions')
      .select('*')
      .eq('child_id', childId)
      .gte('start_time', queryStart.toISOString())
      .lte('start_time', queryEnd.toISOString())
        .order('start_time', { ascending: true });

      if (sleepError) throw sleepError;
      if (napError) throw napError;

      // Compare actual sessions with planned schedule
      const updatedSchedule = compareActualVsPlanned(
        plannedSchedule,
        sleepSessions || [],
        napSessions || []
      );

      setScheduleItems(updatedSchedule);
      
      // Calculate real sleep metrics from actual sessions
      const realMetrics = calculateSleepMetrics(
        sleepSessions || [],
        napSessions || [],
        childAgeMonths
      );
      setSleepMetrics(realMetrics);

      // Calculate adherence metrics
      const adherenceMetrics = calculateMetrics(updatedSchedule);
      setMetrics(adherenceMetrics);

      logger.info('Loaded and compared actual sessions', { 
        childId, 
        sleepCount: sleepSessions?.length || 0,
        napCount: napSessions?.length || 0,
        realMetrics 
      });
    } catch (error) {
      logger.error('Error loading actual sessions', { error, childId });
      // Still set the planned schedule even if we can't load actuals
      setScheduleItems(plannedSchedule);
    }
  };

  const compareActualVsPlanned = (
    planned: ScheduleItem[],
    sleepSessions: any[],
    napSessions: any[]
  ): ScheduleItem[] => {
    // Combine all sleep records (night sleep + naps) for matching
    const allSleepRecords = [...sleepSessions, ...napSessions].map((record, idx) => ({
      ...record,
      originalIndex: idx
    }));
    
    const hasAnyLogsForDay = allSleepRecords.length > 0;
    
    // Track which sessions have been matched to avoid double-matching
    const matchedRecords = new Set<number>();
    
    // Track time adjustments for adaptive scheduling
    let cumulativeTimeShift = 0; // in milliseconds
    
    // Build updated schedule - we'll track results as we go
    const updatedSchedule: ScheduleItem[] = [];

    planned.forEach((item, index) => {
      let actualSession = null;
      let matchedLogId: string | undefined;
      let status: ScheduleStatus = 'on-time';
      let closestOutsideThreshold: { id: string; diffMinutes: number } | undefined;
      
      // CORE MATCHING LOGIC:
      // - For "wake" items: find sleep record whose END_TIME is closest to planned time
      // - For "sleep" items (nap/bedtime): find sleep record whose START_TIME is closest to planned time
      // - Use 60-minute tolerance window
      
      // First pass: Find in-window match
      let inWindowRecord = null;
      let inWindowDiff = Infinity;
      let inWindowIndex = -1;
      
      // Second pass: Find closest outside-window record
      let outsideWindowRecord = null;
      let outsideWindowDiff = Infinity;
      
      if (item.type === 'wake') {
        // AWAKE = end of a sleep session
        allSleepRecords.forEach((record, idx) => {
          if (!record.end_time) return;
          
          const recordEnd = new Date(record.end_time);
          const plannedTime = new Date(item.scheduledStartTime);
          const diffMinutes = Math.abs(recordEnd.getTime() - plannedTime.getTime()) / (1000 * 60);
          
          // Check for in-window match (not already matched)
          if (!matchedRecords.has(idx) && diffMinutes <= 60 && diffMinutes < inWindowDiff) {
            inWindowDiff = diffMinutes;
            inWindowRecord = record;
            inWindowIndex = idx;
          }
          
          // Track closest outside window (regardless of match status)
          if (diffMinutes > 60 && diffMinutes < outsideWindowDiff) {
            outsideWindowDiff = diffMinutes;
            outsideWindowRecord = record;
          }
        });
        
        if (inWindowRecord) {
          actualSession = {
            id: inWindowRecord.id,
            start_time: inWindowRecord.end_time,
            end_time: inWindowRecord.end_time,
            total_duration: inWindowRecord.total_duration
          };
          matchedLogId = inWindowRecord.id;
          matchedRecords.add(inWindowIndex);
          
          logger.debug('Wake matched to sleep end_time', {
            wakeLabel: item.label,
            plannedTime: item.scheduledStartTime,
            actualTime: inWindowRecord.end_time,
            diffMinutes: Math.round(inWindowDiff),
            logId: inWindowRecord.id
          });
        }
      } else if (item.type === 'nap' || item.type === 'bedtime') {
        // SLEEP (nap or bedtime) = start of a sleep session
        allSleepRecords.forEach((record, idx) => {
          if (!record.start_time) return;
          
          const recordStart = new Date(record.start_time);
          const plannedTime = new Date(item.scheduledStartTime);
          const diffMinutes = Math.abs(recordStart.getTime() - plannedTime.getTime()) / (1000 * 60);
          
          // Check for in-window match (not already matched)
          if (!matchedRecords.has(idx) && diffMinutes <= 60 && diffMinutes < inWindowDiff) {
            inWindowDiff = diffMinutes;
            inWindowRecord = record;
            inWindowIndex = idx;
          }
          
          // Track closest outside window (regardless of match status)
          if (diffMinutes > 60 && diffMinutes < outsideWindowDiff) {
            outsideWindowDiff = diffMinutes;
            outsideWindowRecord = record;
          }
        });
        
        if (inWindowRecord) {
          actualSession = inWindowRecord;
          matchedLogId = inWindowRecord.id;
          matchedRecords.add(inWindowIndex);
          
          logger.debug('Sleep matched to sleep start_time', {
            sleepLabel: item.label,
            plannedTime: item.scheduledStartTime,
            actualTime: inWindowRecord.start_time,
            diffMinutes: Math.round(inWindowDiff),
            logId: inWindowRecord.id
          });
        }
      }
      
      // Set closest outside threshold metadata if available
      if (outsideWindowRecord && !inWindowRecord) {
        closestOutsideThreshold = {
          id: outsideWindowRecord.id,
          diffMinutes: Math.round(outsideWindowDiff)
        };
      }

      if (actualSession) {
        // IN-WINDOW MATCH FOUND
        const actualStart = new Date(actualSession.start_time);
        const plannedStart = new Date(item.scheduledStartTime);
        const diffMs = actualStart.getTime() - plannedStart.getTime();
        const diffMinutes = diffMs / (1000 * 60);

        // Determine status based on timing
        if (Math.abs(diffMinutes) <= 30) {
          status = 'on-time';
        } else if (diffMinutes > 30) {
          status = 'late';
        } else {
          status = 'on-time'; // Early is still acceptable
        }

        // Check duration variance for naps - important for sleep quality
        if (item.type === 'nap' && actualSession.total_duration) {
          const actualDurationMinutes = actualSession.total_duration;
          const plannedDurationMinutes = item.scheduledEndTime ? 
            (new Date(item.scheduledEndTime).getTime() - new Date(item.scheduledStartTime).getTime()) / (1000 * 60) : 
            null;
          
          // Pediatric standard: nap <75% of planned is considered short
          if (plannedDurationMinutes && actualDurationMinutes < plannedDurationMinutes * 0.75) {
            status = 'short';
          }
        }
        
        // Update cumulative time shift for adaptive scheduling
        // Only apply shifts > 15 minutes to avoid micro-adjustments
        if (Math.abs(diffMinutes) > 15) {
          cumulativeTimeShift += diffMs;
          logger.debug('Time shift detected', { 
            item: item.label, 
            diffMinutes: Math.round(diffMinutes), 
            cumulativeShiftMinutes: Math.round(cumulativeTimeShift / (1000 * 60))
          });
        }

        const updatedItem: ScheduleItem = {
          ...item,
          actualStartTime: actualSession.start_time,
          actualEndTime: actualSession.end_time,
          status,
          meta: {
            matchedLogId,
            hasAnyLogsForDay
          }
        };
        updatedSchedule.push(updatedItem);
      } else {
        // NO IN-WINDOW MATCH FOUND
        const now = new Date();
        const planned = new Date(item.scheduledStartTime);
        const gracePeriod = 30 * 60 * 1000; // 30 minutes in ms
        
        // Determine status: OffSchedule, NotLogged, or Missed
        if (hasAnyLogsForDay && closestOutsideThreshold) {
          // Logs exist but outside threshold
          status = 'off-schedule';
          logger.info('Off-schedule detected', {
            item: item.label,
            closestLogId: closestOutsideThreshold.id,
            diffMinutes: closestOutsideThreshold.diffMinutes
          });
        } else if (!hasAnyLogsForDay) {
          // No logs at all for the day
          status = 'not-logged';
          logger.info('Not logged', { item: item.label });
        } else if (now.getTime() > planned.getTime() + gracePeriod) {
          // Past the grace period - mark as missed
          status = 'missed';
          logger.info('Missed', { item: item.label });
        }
        
        // ADAPTIVE SCHEDULING: Apply cumulative time shift to future items
        if (now.getTime() < planned.getTime() && cumulativeTimeShift !== 0) {
          const adjustedStartTime = new Date(planned.getTime() + cumulativeTimeShift).toISOString();
          const adjustedEndTime = item.scheduledEndTime 
            ? new Date(new Date(item.scheduledEndTime).getTime() + cumulativeTimeShift).toISOString()
            : undefined;
          
          logger.info('Adaptive scheduling applied', {
            item: item.label,
            originalStart: item.scheduledStartTime,
            adjustedStart: adjustedStartTime,
            shiftMinutes: Math.round(cumulativeTimeShift / (1000 * 60))
          });
          
          updatedSchedule.push({
            ...item,
            scheduledStartTime: adjustedStartTime,
            scheduledEndTime: adjustedEndTime,
            actualStartTime: undefined,
            actualEndTime: undefined,
            status: status === 'not-logged' || status === 'off-schedule' ? status : 'on-time',
            meta: {
              closestOutsideThreshold,
              hasAnyLogsForDay
            }
          });
          return;
        }
        
        // Add item with appropriate status and metadata
        updatedSchedule.push({
          ...item,
          actualStartTime: undefined,
          actualEndTime: undefined,
          status,
          meta: {
            closestOutsideThreshold,
            hasAnyLogsForDay
          }
        });
      }
    });
    
    return updatedSchedule;
  };

  const generateAIRecommendation = async (childId: string, date?: Date) => {
    setIsLoadingRecommendation(true);
    try {
      const targetDate = date || new Date();
      const schedule = await ScheduleRecommendationService.generateRecommendation(childId, targetDate);
      
      if (schedule) {
        const dateKey = targetDate.toISOString().split('T')[0];
        setCustomSchedules(prev => {
          const newMap = new Map(prev);
          newMap.set(dateKey, schedule);
          return newMap;
        });
        
        setScheduleItems(schedule);
        const newMetrics = calculateMetrics(schedule);
        setMetrics(newMetrics);
        
        logger.info('AI schedule recommendation applied', { childId, dateKey, itemCount: schedule.length });
      }
    } catch (error) {
      logger.error('Error generating AI recommendation', { error, childId });
    } finally {
      setIsLoadingRecommendation(false);
    }
  };
  
  // New function to create a schedule item
  const createScheduleItem = async (item: Partial<ScheduleItem>): Promise<ScheduleItem> => {
    // In a real app, this would send a request to the API
    console.log('Creating schedule item:', item);
    
    // Mock implementation
    const newItem: ScheduleItem = {
      id: `new-${Date.now()}`,
      type: item.type || 'wake',
      label: item.label || '',
      scheduledStartTime: item.scheduledStartTime || new Date().toISOString(),
      scheduledEndTime: item.scheduledEndTime,
      status: 'on-time',
      ...item
    };
    
    // Add to local state
    setScheduleItems(prev => [...prev, newItem]);
    return newItem;
  };
  
  // New function to update the entire schedule
  const updateSchedule = async (items: ScheduleItem[], childId?: string, caregiverIdParam?: string, repeatDays: number = 1): Promise<void> => {
    logger.info(`${LOG_PREFIX} updateSchedule called`, {
      itemsCount: items?.length,
      childId,
      caregiverIdParam,
      contextCaregiverId: caregiverIdState,
      isSpecialist,
      firstItemTime: items[0]?.scheduledStartTime,
      repeatDays,
      hasItems: items && items.length > 0
    });
    
    try {
      // Extract date from the first item to determine which date's schedule to update
      const firstItemDate = items[0]?.scheduledStartTime?.split('T')[0];
      logger.debug(`${LOG_PREFIX} Extracted schedule date`, { firstItemDate });
      
      if (!firstItemDate || !childId) {
        const errorMsg = `Missing required data: firstItemDate=${firstItemDate}, childId=${childId}`;
        logger.error(`${LOG_PREFIX} Validation failed`, { firstItemDate, childId });
        throw new Error(errorMsg);
      }

      const effectiveCaregiverId = caregiverIdParam || caregiverIdState;
      logger.debug(`${LOG_PREFIX} Resolved caregiver ID`, { 
        effectiveCaregiverId, 
        source: caregiverIdParam ? 'param' : 'state' 
      });
      
      if (!effectiveCaregiverId) {
        const errorMsg = `Caregiver ID is required. Received: caregiverIdParam=${caregiverIdParam}, contextCaregiverId=${caregiverIdState}`;
        logger.error(`${LOG_PREFIX} No caregiver ID available`, { caregiverIdParam, caregiverIdState });
        throw new Error(errorMsg);
      }

      // Get specialist ID if user is a specialist
      let specialistId = null;
      if (isSpecialist) {
        logger.debug(`${LOG_PREFIX} Fetching specialist ID`);
        const { data: specialist } = await supabase
          .from('specialists')
          .select('id')
          .eq('auth_user_id', (await supabase.auth.getUser()).data.user?.id)
          .single();
        specialistId = specialist?.id;
        logger.info(`${LOG_PREFIX} Specialist ID resolved`, { specialistId });
      }

      // Save custom schedule for multiple days
      const startDate = new Date(firstItemDate);
      const upsertPromises = [];

      logger.info(`${LOG_PREFIX} Creating schedule for ${repeatDays} day(s)`, { 
        startDate: firstItemDate,
        childId,
        caregiverId: effectiveCaregiverId,
        specialistId
      });

      for (let i = 0; i < repeatDays; i++) {
        const targetDate = new Date(startDate);
        targetDate.setDate(startDate.getDate() + i);
        const dateStr = targetDate.toISOString().split('T')[0];
        
        logger.debug(`${LOG_PREFIX} Upserting schedule for date`, { dateStr, dayOffset: i });
        
        upsertPromises.push(
          supabase
            .from('custom_sleep_schedules' as any)
            .upsert({
              child_id: childId,
              caregiver_id: effectiveCaregiverId,
              specialist_id: specialistId,
              schedule_date: dateStr,
              schedule_items: items,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'child_id,schedule_date'
            })
        );
      }

      logger.info(`${LOG_PREFIX} Executing ${upsertPromises.length} upsert operations`);
      const results = await Promise.all(upsertPromises);
      
      // Check for errors
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        logger.error(`${LOG_PREFIX} Errors during schedule upsert`, { 
          errorCount: errors.length,
          errors: errors.map(e => e.error)
        });
        throw new Error(`Failed to save schedule: ${errors[0].error?.message}`);
      }
      
      logger.info(`${LOG_PREFIX} Schedule saved successfully`, { 
        daysUpdated: results.length,
        childId,
        startDate: firstItemDate
      });

      // Update local state with the new schedule for the current date
      setCustomSchedules(prev => {
        const newMap = new Map(prev);
        for (let i = 0; i < repeatDays; i++) {
          const targetDate = new Date(startDate);
          targetDate.setDate(startDate.getDate() + i);
          const dateStr = targetDate.toISOString().split('T')[0];
          newMap.set(dateStr, items);
        }
        return newMap;
      });
      
      setScheduleItems(items);
      setHasCustomSchedule(true);
      
      const newMetrics = calculateMetrics(items);
      setMetrics(newMetrics);
      
      logger.info(`${LOG_PREFIX} Schedule update completed`, { 
        itemsCount: items.length,
        daysUpdated: repeatDays
      });
    } catch (error) {
      logger.error(`${LOG_PREFIX} updateSchedule failed`, { 
        error, 
        childId, 
        caregiverIdParam,
        itemsCount: items?.length 
      });
      throw error;
    }
  };

  // New function to reset to AI recommendations
  const resetToRecommendations = async (childId: string, date?: Date): Promise<void> => {
    try {
      const targetDate = date || new Date();
      const dateKey = targetDate.toISOString().split('T')[0];

      // Delete custom schedule from database
      const { error } = await supabase
        .from('custom_sleep_schedules' as any)
        .delete()
        .eq('child_id', childId)
        .eq('schedule_date', dateKey);

      if (error) throw error;

      // Remove from local cache
      setCustomSchedules(prev => {
        const newMap = new Map(prev);
        newMap.delete(dateKey);
        return newMap;
      });

      // Regenerate AI recommendation
      await generateAIRecommendation(childId, targetDate);

      logger.info('Reset to AI recommendations', { childId, date: dateKey });
    } catch (error) {
      logger.error('Error resetting to recommendations', { error, childId });
      throw error;
    }
  };
  
  // Load initial data when component mounts
  React.useEffect(() => {
    loadScheduleForDate(new Date());
  }, []);
  
  const value = {
    scheduleItems,
    setScheduleItems,
    ...metrics,
    sleepMetrics,
    childInfo,
    caregiverId: caregiverIdState,
    loadScheduleForDate,
    generateAIRecommendation,
    createScheduleItem,
    updateSchedule,
    resetToRecommendations,
    isSpecialist,
    isLoadingRecommendation,
    hasCustomSchedule
  };
  
  return (
    <SleepScheduleContext.Provider value={value}>
      {children}
    </SleepScheduleContext.Provider>
  );
}

export const useSleepSchedule = () => useContext(SleepScheduleContext);
